import type { TourStep } from '@/components/ui/Tour';
import type { StartTourWithCompletionKeyOptions } from '@/components/tour/PageTourHost';
import type { PageTourKey } from './registry';
import {
  type StudentDetailTourContext,
  type StudentsListTourOptions,
  getStudentDetailTourSteps,
  getStudentsListTourSteps,
} from './students-management-tour-steps';
import { schedulePageTourStart, scheduleTourStepsStart } from './scheduleAutoPageTour';

export type { StudentDetailTourContext, StudentsListTourOptions };

export const STUDENTS_LIST_TOUR_KEY: PageTourKey = 'academy-students-list';
export const STUDENT_DETAIL_TOUR_KEY: PageTourKey = 'academy-student-detail';

export interface StudentsListHelpContent {
  title: string;
  description: string;
  tourLabel: string;
  tourDescription: string;
}

export const STUDENTS_LIST_HELP: StudentsListHelpContent = {
  title: 'Students',
  description:
    'Search and filter your roster. Students appear from bookings and enrolments, or add them manually and import CSVs for bulk setup.',
  tourLabel: 'Tour this page',
  tourDescription: 'Find students, add manually, and import your roster.',
};

export interface StudentDetailHelpContent {
  title: string;
  description: string;
  tourLabel: string;
  tourDescription: string;
}

export const STUDENT_DETAIL_HELP: StudentDetailHelpContent = {
  title: 'Student profile',
  description:
    'Review roster details, attendance, progress, enrolled lessons, make-up credits, and coach notes for this player.',
  tourLabel: 'Tour this profile',
  tourDescription: 'Walk through each tab and the profile information cards.',
};

export function scheduleStudentsListTourStart(
  startTourWithCompletionKey: (
    completionKey: string,
    steps: TourStep[],
    options?: StartTourWithCompletionKeyOptions,
  ) => void,
  options: StudentsListTourOptions = {},
): () => void {
  const steps = getStudentsListTourSteps(options);
  return scheduleTourStepsStart(
    () =>
      startTourWithCompletionKey(STUDENTS_LIST_TOUR_KEY, steps, {
        onFinish: options.onFinish,
      }),
    steps,
  );
}

export function scheduleStudentDetailTourStart(
  startTourWithCompletionKey: (completionKey: string, steps: TourStep[]) => void,
  ctx: StudentDetailTourContext,
): () => void {
  return schedulePageTourStart(
    () => startTourWithCompletionKey(STUDENT_DETAIL_TOUR_KEY, getStudentDetailTourSteps(ctx)),
    STUDENT_DETAIL_TOUR_KEY,
  );
}
